// Multi-step booking modal for the Editorial Warm marketing surface.
//
// Visually a verbatim port of tmp/handoff/design/Nala Home.html →
// `src/booking-modal.jsx`. Behaviorally, the "confirm" step posts to
// the existing /leads endpoint (same call powering LeadCaptureForm),
// because the public marketing site doesn't have an authenticated
// booking session — leads get triaged into real Appointments by CSRs.
//
// All inputs are bundled into the lead's `notes` field so nothing is
// lost (the lead schema has no native columns for service/zip/slot).

import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { useMutation } from '@tanstack/react-query'
import { submitLead } from '../../services/leads'
import { trackBookVisitConversion } from '../../utils/analytics'
import { loadGoogleMaps } from '../../lib/loadGoogleMaps'
import { NalaIcon } from './NalaIcon'
import NalaAuthOptions from './NalaAuthOptions'
import { nala } from './palette'

interface BookingModalProps {
  open: boolean
  onClose: () => void
}

interface BookingData {
  petName: string
  petKind: 'Dog' | 'Cat'
  service: string
  address: string
  addressStreet: string
  addressCity: string
  addressState: string
  addressZip: string
  day: number
  slot: string
  name: string
  phone: string
  email: string
  notes: string
}

const SERVICES = [
  { name: 'Wellness exam',           price: '$150'           },
  { name: 'Urgent visit',            price: '$200'           },
  { name: 'Senior & chronic care',   price: '$300/month'     },
  { name: 'Microchipping',           price: '$75'            },
  { name: 'End-of-life care',        price: '$450'           },
  { name: 'International certificate', price: '$350 + USDA fees' },
] as const

const TIME_WINDOWS = [
  { key: 'morning',   label: 'Morning',   hours: '8:00 AM – 1:00 PM' },
  { key: 'afternoon', label: 'Afternoon', hours: '1:00 PM – 5:00 PM' },
  { key: 'evening',   label: 'Evening',   hours: '5:00 PM – 9:00 PM' },
] as const

const TITLES = [
  'Tell us about your pet',
  'Where should we visit?',
  'Pick a time',
  'Almost done',
] as const

const SUBTITLES = [
  'A few details so the right vet shows up.',
  'We serve homes across Miami-Dade.',
  'All visits include a full home consultation.',
  "We'll text a confirmation and a 30-min heads-up.",
] as const

export default function BookingModal({ open, onClose }: BookingModalProps) {
  const [step, setStep] = useState(0)
  const [data, setData] = useState<BookingData>({
    petName: '',
    petKind: 'Dog',
    service: 'Wellness exam',
    address: '',
    addressStreet: '',
    addressCity: '',
    addressState: '',
    addressZip: '',
    day: 1,
    slot: '',
    name: '',
    phone: '',
    email: '',
    notes: '',
  })
  const addressInputRef = useRef<HTMLInputElement>(null)

  const set = <K extends keyof BookingData>(k: K, v: BookingData[K]) =>
    setData((d) => ({ ...d, [k]: v }))

  // Reset to first step (and clear submission state) every time the modal
  // is reopened, so a returning visitor always sees a fresh flow.
  useEffect(() => {
    if (open) {
      setStep(0)
      mutation.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Attach Google Places Autocomplete to the address <input> as a progressive
  // enhancement. The input itself is the source of truth for data.address, so
  // the step is always completable by typing manually — even if Maps never
  // loads (no API key, blocked, offline). loadGoogleMaps() resolves false when
  // there's no usable key, in which case we simply skip attaching.
  useEffect(() => {
    if (step !== 1) return
    let cancelled = false
    let cleanup: (() => void) | undefined

    loadGoogleMaps().then((ok) => {
      if (cancelled || !ok || !addressInputRef.current) return
      const places = (window as any).google?.maps?.places
      if (!places?.Autocomplete) return

      // Ensure the prediction dropdown renders above the modal overlay.
      const styleId = 'gmp-autocomplete-styles'
      if (!document.getElementById(styleId)) {
        const style = document.createElement('style')
        style.id = styleId
        style.textContent = `.pac-container { z-index: 99999 !important; }`
        document.head.appendChild(style)
      }

      const autocomplete = new places.Autocomplete(addressInputRef.current, {
        componentRestrictions: { country: 'us' },
        types: ['address'],
        fields: ['address_components', 'formatted_address'],
      })

      const listener = autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace()
        const comps = place.address_components ?? []
        const get = (type: string) =>
          comps.find((c: any) => c.types.includes(type))?.long_name ?? ''
        const getShort = (type: string) =>
          comps.find((c: any) => c.types.includes(type))?.short_name ?? ''

        const streetNumber = get('street_number')
        const route = get('route')
        const city = get('locality') || get('sublocality')
        const state = getShort('administrative_area_level_1')
        const zip = get('postal_code')

        const fullAddress = place.formatted_address
          || `${streetNumber} ${route}, ${city}, ${state} ${zip}`.trim()

        setData((d) => ({
          ...d,
          address: fullAddress,
          addressStreet: `${streetNumber} ${route}`.trim(),
          addressCity: city,
          addressState: state,
          addressZip: zip,
        }))
      })

      cleanup = () => {
        listener?.remove?.()
        ;(window as any).google?.maps?.event?.clearInstanceListeners?.(autocomplete)
      }
    })

    return () => {
      cancelled = true
      cleanup?.()
    }
  }, [step])

  // 7-day strip starting today; computed inside render so "Today" stays
  // truthful across midnight boundaries without a clock effect.
  const days = useMemo(() => {
    const today = new Date()
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(today)
      d.setDate(today.getDate() + i)
      return d
    })
  }, [open])

  const stepValid = [
    !!data.petName.trim() && !!data.petKind && !!data.service,
    !!data.address.trim(),
    !!data.slot,
    !!data.name.trim() && !!data.phone.trim() && data.email.includes('@'),
  ][step]

  const mutation = useMutation({
    mutationFn: () => {
      const [first_name, ...rest] = data.name.trim().split(/\s+/)
      const preferredDay = days[data.day]
      return submitLead({
        first_name: first_name || data.name,
        last_name:  rest.join(' ') || undefined,
        email:      data.email || undefined,
        phone:      data.phone || undefined,
        pet_name:   data.petName || undefined,
        pet_species: data.petKind.toLowerCase(),
        // Address fields parsed from Google Places autocomplete
        address_street: data.addressStreet || undefined,
        address_city:   data.addressCity || undefined,
        address_state:  data.addressState || undefined,
        address_zip:    data.addressZip || undefined,
        // Structured booking fields — create a pending Appointment server-side
        service_name:     data.service,
        preferred_date:   preferredDay.toISOString().split('T')[0],
        preferred_window: data.slot,
        notes:      data.notes || undefined,
        source:     'booking_modal',
        market_slug: 'miami',
      })
    },
    onSuccess: () => {
      // Push the "Book a visit" conversion into the GTM dataLayer so the
      // container can fire the Google Ads / GA4 conversion tag.
      trackBookVisitConversion({
        source: 'booking_modal',
        service: data.service,
        petSpecies: data.petKind.toLowerCase(),
        preferredWindow: data.slot,
      })
    },
  })

  const next = () => {
    if (step < 3) {
      setStep((s) => s + 1)
    } else {
      mutation.mutate()
    }
  }
  const back = () => setStep((s) => Math.max(0, s - 1))

  if (!open) return null

  const submitted = mutation.isSuccess

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(20,18,15,0.55)',
        backdropFilter: 'blur(3px)',
        padding: 24,
        fontFamily: nala.sans,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(560px, 100%)',
          maxHeight: '92vh',
          overflow: 'auto',
          background: nala.surface,
          color: nala.ink,
          borderRadius: nala.radius,
          boxShadow: '0 30px 80px rgba(0,0,0,0.35)',
          border: `1px solid ${nala.line}`,
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '18px 22px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${nala.line}`,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: nala.accent,
                color: nala.accentInk,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: nala.display,
                fontWeight: 700,
                fontSize: 14,
              }}
            >
              N
            </div>
            <div style={{ fontFamily: nala.display, fontSize: 17, fontWeight: 600 }}>
              Book a house call
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close booking"
            style={{
              all: 'unset',
              cursor: 'pointer',
              color: nala.mute,
              fontSize: 20,
              lineHeight: 1,
              padding: 6,
            }}
          >
            ×
          </button>
        </div>

        {/* Stepper */}
        {!submitted && (
          <div style={{ display: 'flex', gap: 6, padding: '14px 22px 0' }}>
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                style={{
                  flex: 1,
                  height: 4,
                  borderRadius: 2,
                  background: i <= step ? nala.accent : nala.line,
                  transition: 'background .25s',
                }}
              />
            ))}
          </div>
        )}

        {!submitted ? (
          <div style={{ padding: '18px 22px 8px' }}>
            <div
              style={{
                fontFamily: nala.display,
                fontSize: 24,
                fontWeight: 500,
                letterSpacing: '-0.01em',
              }}
            >
              {TITLES[step]}
            </div>
            <div style={{ color: nala.mute, fontSize: 13.5, marginTop: 4 }}>
              {SUBTITLES[step]}
            </div>

            <div style={{ marginTop: 18 }}>
              {step === 0 && (
                <div style={{ display: 'grid', gap: 14 }}>
                  <Field label="Pet's name">
                    <Input
                      value={data.petName}
                      onChange={(v) => set('petName', v)}
                      placeholder="e.g. Luna"
                    />
                  </Field>
                  <Field label="Species">
                    <Segmented
                      options={['Dog', 'Cat']}
                      value={data.petKind}
                      onChange={(v) => set('petKind', v as 'Dog' | 'Cat')}
                    />
                  </Field>
                  <Field label="Reason for visit">
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      {SERVICES.map((s) => {
                        const selected = data.service === s.name
                        return (
                          <button
                            key={s.name}
                            type="button"
                            onClick={() => set('service', s.name)}
                            style={{
                              all: 'unset',
                              cursor: 'pointer',
                              padding: '12px 14px',
                              borderRadius: 12,
                              border: `1px solid ${selected ? nala.accent : nala.line}`,
                              background: selected ? nala.soft : 'transparent',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 2,
                            }}
                          >
                            <div style={{ fontWeight: 600, fontSize: 14 }}>{s.name}</div>
                            <div style={{ color: nala.mute, fontSize: 12 }}>
                              {s.price}
                            </div>
                          </button>
                        )
                      })}
                    </div>
                  </Field>
                </div>
              )}

              {step === 1 && (
                <div style={{ display: 'grid', gap: 14 }}>
                  <Field label="Your address">
                    <input
                      ref={addressInputRef}
                      type="text"
                      value={data.address}
                      onChange={(e) =>
                        setData((d) => ({
                          ...d,
                          address: e.target.value,
                          addressStreet: '',
                          addressCity: '',
                          addressState: '',
                          addressZip: '',
                        }))
                      }
                      placeholder="Start typing your street address…"
                      autoComplete="off"
                      style={{
                        width: '100%',
                        padding: '11px 14px',
                        borderRadius: 10,
                        border: `1px solid ${nala.line}`,
                        background: '#fff',
                        color: nala.ink,
                        fontSize: 14,
                        fontFamily: 'inherit',
                        outline: 'none',
                      }}
                      onFocus={(e) => (e.currentTarget.style.borderColor = nala.accent)}
                      onBlur={(e) => (e.currentTarget.style.borderColor = nala.line)}
                    />
                    {data.addressCity && data.addressState && (
                      <div style={{ marginTop: 6, fontSize: 12, color: nala.mute }}>
                        ✓ {data.addressStreet}, {data.addressCity}, {data.addressState} {data.addressZip}
                      </div>
                    )}
                  </Field>
                  <Field label="Notes for the vet (optional)">
                    <Textarea
                      value={data.notes}
                      onChange={(v) => set('notes', v)}
                      placeholder="Parking, building access, pet's temperament…"
                    />
                  </Field>
                </div>
              )}

              {step === 2 && (
                <div>
                  <div
                    style={{
                      display: 'flex',
                      gap: 8,
                      overflowX: 'auto',
                      paddingBottom: 8,
                      marginBottom: 14,
                    }}
                  >
                    {days.map((d, i) => {
                      const dayName = d.toLocaleDateString('en-US', { weekday: 'short' })
                      const num = d.getDate()
                      const sel = data.day === i
                      return (
                        <button
                          key={i}
                          type="button"
                          onClick={() => {
                            set('day', i)
                            set('slot', '')
                          }}
                          style={{
                            all: 'unset',
                            cursor: 'pointer',
                            padding: '10px 14px',
                            borderRadius: 12,
                            border: `1px solid ${sel ? nala.accent : nala.line}`,
                            background: sel ? nala.accent : 'transparent',
                            color: sel ? nala.accentInk : nala.ink,
                            textAlign: 'center',
                            minWidth: 64,
                          }}
                        >
                          <div
                            style={{
                              fontSize: 11,
                              opacity: 0.8,
                              textTransform: 'uppercase',
                              letterSpacing: '0.06em',
                            }}
                          >
                            {i === 0 ? 'Today' : dayName}
                          </div>
                          <div
                            style={{
                              fontFamily: nala.display,
                              fontSize: 20,
                              fontWeight: 600,
                              marginTop: 2,
                            }}
                          >
                            {num}
                          </div>
                        </button>
                      )
                    })}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 }}>
                    {TIME_WINDOWS.map((w) => {
                      const sel = data.slot === w.key
                      return (
                        <button
                          key={w.key}
                          type="button"
                          onClick={() => set('slot', w.key)}
                          style={{
                            all: 'unset',
                            cursor: 'pointer',
                            padding: '12px 8px',
                            borderRadius: 10,
                            textAlign: 'left',
                            border: `1px solid ${sel ? nala.accent : nala.line}`,
                            background: sel ? nala.soft : 'transparent',
                          }}
                        >
                          <div style={{ fontWeight: 600, fontSize: 13, color: nala.ink }}>{w.label}</div>
                          <div style={{ fontSize: 11, color: nala.mute, marginTop: 2 }}>{w.hours}</div>
                        </button>
                      )
                    })}
                  </div>
                  <div style={{ marginTop: 14, fontSize: 12, color: nala.mute }}>
                    The vet will confirm an exact arrival time within your selected window.
                  </div>
                </div>
              )}

              {step === 3 && (
                <div style={{ display: 'grid', gap: 14 }}>
                  <Field label="Your name">
                    <Input
                      value={data.name}
                      onChange={(v) => set('name', v)}
                      placeholder="Mia Chen"
                    />
                  </Field>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <Field label="Phone">
                      <Input
                        value={data.phone}
                        onChange={(v) => set('phone', v)}
                        placeholder="(305) 555-0184"
                      />
                    </Field>
                    <Field label="Email">
                      <Input
                        value={data.email}
                        onChange={(v) => set('email', v)}
                        placeholder="mia@example.com"
                      />
                    </Field>
                  </div>
                  <div
                    style={{
                      marginTop: 4,
                      padding: 14,
                      background: nala.soft,
                      borderRadius: 12,
                      fontSize: 13.5,
                      lineHeight: 1.5,
                    }}
                  >
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Visit summary</div>
                    <Row k="Pet" v={`${data.petName || '—'} (${data.petKind})`} />
                    <Row k="Service" v={data.service} />
                    <Row k="Address" v={data.address || '—'} />
                    <Row
                      k="When"
                      v={`${days[data.day].toLocaleDateString('en-US', {
                        weekday: 'long',
                        month: 'short',
                        day: 'numeric',
                      })} · ${TIME_WINDOWS.find((w) => w.key === data.slot)?.label || data.slot || '—'}`}
                    />
                  </div>
                  {mutation.isError && (
                    <div
                      style={{
                        padding: '10px 12px',
                        borderRadius: 10,
                        background: '#fbe9df',
                        color: nala.clay,
                        fontSize: 13,
                      }}
                    >
                      We couldn&apos;t submit just now. Please try again, or text us directly.
                    </div>
                  )}
                  <p style={{ fontSize: 11, color: nala.mute, lineHeight: 1.5 }}>
                    By tapping &ldquo;Confirm booking,&rdquo; you agree to receive appointment and
                    account text messages from Nala at the number provided (including confirmations
                    and reminders). Consent is not a condition of service. Msg frequency varies; msg
                    &amp; data rates may apply. Reply STOP to opt out, HELP for help. See our{' '}
                    <a href="/policy" style={{ color: nala.mute, textDecoration: 'underline' }}>
                      Privacy Policy
                    </a>{' '}
                    and{' '}
                    <a href="/terms" style={{ color: nala.mute, textDecoration: 'underline' }}>
                      Terms
                    </a>
                    .
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : (
          // Two-zone success view, mirroring the Editorial Warm screenshot:
          //   1. Cream confirmation panel (visit summary)
          //   2. White "Create your Nala account" panel with five sign-in
          //      options + Close.
          // The booking already persisted to /leads above; everything below
          // is *post*-booking — failure here doesn't lose the appointment.
          <>
            <div
              style={{
                padding: '32px 24px 28px',
                background: nala.surface,
                borderBottom: `1px solid ${nala.line}`,
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  border: `1.5px solid ${nala.accent}`,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 14,
                }}
              >
                <NalaIcon.Check size={24} color={nala.accent} />
              </div>
              <div
                style={{
                  fontFamily: nala.display,
                  fontSize: 28,
                  fontWeight: 500,
                  letterSpacing: '-0.01em',
                  color: nala.ink,
                }}
              >
                You&apos;re on the list.
              </div>
              <div
                style={{
                  color: nala.mute,
                  fontSize: 14,
                  marginTop: 8,
                  lineHeight: 1.55,
                  maxWidth: 380,
                  marginLeft: 'auto',
                  marginRight: 'auto',
                }}
              >
                We&apos;ll text {data.phone || 'you'} a confirmation, and again 30 minutes before{' '}
                {days[data.day].toLocaleDateString('en-US', { weekday: 'long' })}&apos;s visit.
              </div>
              <div
                style={{
                  display: 'inline-block',
                  marginTop: 18,
                  padding: '10px 18px',
                  background: '#fff',
                  border: `1px solid ${nala.line}`,
                  borderRadius: 999,
                  fontSize: 13,
                  color: nala.ink,
                }}
              >
                {days[data.day].toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'short',
                  day: 'numeric',
                })}{' '}
                · {TIME_WINDOWS.find((w) => w.key === data.slot)?.label ?? data.slot} · {data.service}
              </div>
            </div>

            <div style={{ padding: '24px 24px 22px' }}>
              <div
                style={{
                  fontWeight: 600,
                  fontSize: 15,
                  color: nala.ink,
                  marginBottom: 2,
                }}
              >
                Create your Nala account
              </div>
              <div style={{ color: nala.mute, fontSize: 13, marginBottom: 16, lineHeight: 1.5 }}>
                Track visits, view invoices, and rebook easily.
              </div>

              <NalaAuthOptions
                email={data.email}
                registerPrefill={{
                  first_name: data.name.trim().split(/\s+/)[0],
                  last_name:  data.name.trim().split(/\s+/).slice(1).join(' '),
                  phone:      data.phone,
                }}
              />

              <div style={{ marginTop: 18, textAlign: 'center' }}>
                <button type="button" onClick={onClose} style={primaryBtn()}>
                  Close
                </button>
              </div>
            </div>
          </>
        )}

        {/* Footer */}
        {!submitted && (
          <div
            style={{
              padding: '14px 22px 22px',
              borderTop: `1px solid ${nala.line}`,
              marginTop: 14,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <button type="button" onClick={step === 0 ? onClose : back} style={ghostBtn()}>
              {step === 0 ? 'Cancel' : 'Back'}
            </button>
            <div style={{ fontSize: 12, color: nala.mute }}>Step {step + 1} of 4</div>
            <button
              type="button"
              onClick={next}
              disabled={!stepValid || mutation.isPending}
              style={{
                ...primaryBtn(),
                opacity: stepValid && !mutation.isPending ? 1 : 0.4,
                cursor: stepValid && !mutation.isPending ? 'pointer' : 'not-allowed',
              }}
            >
              {step === 3
                ? mutation.isPending
                  ? 'Booking…'
                  : 'Confirm booking'
                : 'Continue'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────
// Form primitives — local because they share the modal's palette + tone
// and we don't want to leak inline-style tokens elsewhere in the app.
// ─────────────────────────────────────────────────────────────────────────

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label style={{ display: 'block' }}>
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: nala.ink,
          marginBottom: 6,
          letterSpacing: '0.02em',
        }}
      >
        {label}
      </div>
      {children}
    </label>
  )
}

function Input({
  value,
  onChange,
  placeholder,
}: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
}) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        width: '100%',
        padding: '11px 14px',
        borderRadius: 10,
        border: `1px solid ${nala.line}`,
        background: '#fff',
        color: nala.ink,
        fontSize: 14,
        fontFamily: 'inherit',
        outline: 'none',
      }}
      onFocus={(e) => (e.target.style.borderColor = nala.accent)}
      onBlur={(e) => (e.target.style.borderColor = nala.line)}
    />
  )
}

function Textarea({
  value,
  onChange,
  placeholder,
}: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
}) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={3}
      style={{
        width: '100%',
        padding: '11px 14px',
        borderRadius: 10,
        border: `1px solid ${nala.line}`,
        background: '#fff',
        color: nala.ink,
        fontSize: 14,
        fontFamily: 'inherit',
        outline: 'none',
        resize: 'vertical',
      }}
    />
  )
}

function Segmented({
  options,
  value,
  onChange,
}: {
  options: string[]
  value: string
  onChange: (v: string) => void
}) {
  return (
    <div
      style={{
        display: 'inline-flex',
        padding: 3,
        background: nala.soft,
        borderRadius: 10,
      }}
    >
      {options.map((o) => (
        <button
          key={o}
          type="button"
          onClick={() => onChange(o)}
          style={{
            all: 'unset',
            cursor: 'pointer',
            padding: '8px 18px',
            borderRadius: 8,
            fontSize: 13.5,
            fontWeight: 500,
            background: value === o ? '#fff' : 'transparent',
            color: nala.ink,
            boxShadow: value === o ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
          }}
        >
          {o}
        </button>
      ))}
    </div>
  )
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '3px 0' }}>
      <span style={{ color: nala.mute }}>{k}</span>
      <span style={{ textAlign: 'right' }}>{v}</span>
    </div>
  )
}

function primaryBtn(): CSSProperties {
  return {
    all: 'unset',
    cursor: 'pointer',
    padding: '10px 18px',
    borderRadius: 999,
    background: nala.accent,
    color: nala.accentInk,
    fontWeight: 600,
    fontSize: 14,
  }
}

function ghostBtn(): CSSProperties {
  return {
    all: 'unset',
    cursor: 'pointer',
    padding: '10px 14px',
    borderRadius: 999,
    color: nala.mute,
    fontSize: 14,
    fontWeight: 500,
  }
}
